import * as React from 'react';
import { IOverflowSetItemProps, ITheme, IStyleFunctionOrObject, styled } from '@fluentui/react';
import { useFFXHostUxIcon } from '@fluidx/icons';
import { getPreviewBodyWithStrings } from '../previewLabel/getPreviewBodyWithStrings';
import { Loading } from '../Loading';
import { HostAppColors } from '../styles/HostAppColors';
import { Locale, LocaleDirection, getUIDirection } from '@fluidx/utilities';
import { IdentityType } from '@fluidx/office-fluid-types';

interface ActionMenuPreviewProps {
  /** The app colors of the hosting app */
  appColors?: HostAppColors;

  /** Host localization */
  localization: {
    /** Host locale */
    locale: Locale;

    /** Host UI direction. If not provided, this will be determined from Host locale */
    localeDirection?: LocaleDirection;
  };

  /** Identity type for customizing text displayed to user */
  identityType: IdentityType;

  /** Callback invoked when clicking on the primary button, i.e "Got It" */
  onPrimaryButtonClick: () => void;

  styles?: IStyleFunctionOrObject<ActionMenuPreviewStyleProps, ActionMenuPreviewStyles>;

  theme?: ITheme;

  previewButtonLabelText: string;
}

interface ActionMenuPreviewStyles {}

interface ActionMenuPreviewStyleProps extends Pick<ActionMenuPreviewProps, 'theme'> {}

// Depending on how we will do theming, in order to show the Preview correctly,
// we will need to convert those colors into HostAppColors.
const defaultAppColors: HostAppColors = {
  themeDarker: '#02494C',
  themeDark: '#026367',
  themeDarkAlt: '#02767a',
  themePrimary: '#038387',
  themeSecondary: '#159196',
  themeTertiary: '#4BB4B7',
  themeLight: '#9BD9DB',
  themeLighter: '#C7EBEC',
  themeLightAlt: '#F0FAFA'
};

const ActionMenuPreviewButton = React.memo(
  styled<ActionMenuPreviewProps, ActionMenuPreviewStyleProps, ActionMenuPreviewStyles>(function ActionMenuPreviewButton(
    props: ActionMenuPreviewProps
  ) {
    const { localization, identityType, onPrimaryButtonClick } = props;

    const PreviewBody = React.useMemo(
      () => React.lazy(() => getPreviewBodyWithStrings(localization.locale, identityType)),
      [localization, identityType]
    );

    return (
      <React.Suspense fallback={<Loading />}>
        <PreviewBody appColors={getAppColors(props)} onPrimaryButtonClick={onPrimaryButtonClick} />
      </React.Suspense>
    );
  },
  {})
);

function getAppColors(props: ActionMenuPreviewProps): HostAppColors {
  const { appColors, theme } = props;

  if (appColors) {
    return appColors;
  }

  if (theme) {
    const palette = theme.palette;
    return {
      themeDarker: palette.themeDarker,
      themeDark: palette.themeDark,
      themeDarkAlt: palette.themeDarkAlt,
      themePrimary: palette.themePrimary,
      themeSecondary: palette.themeSecondary,
      themeTertiary: palette.themeTertiary,
      themeLight: palette.themeLight,
      themeLighter: palette.themeLighter,
      themeLightAlt: palette.themeLighterAlt // This is the only item which doesn't line up exactly
    };
  }

  return defaultAppColors;
}

export const getActionMenuPreviewItem = (previewProps: ActionMenuPreviewProps): IOverflowSetItemProps => {
  const localeDirection = previewProps.localization.localeDirection || getUIDirection(previewProps.localization.locale);

  const previewItemProps: IOverflowSetItemProps = {
    key: 'preview',
    name: previewProps.previewButtonLabelText,
    iconProps: { iconName: useFFXHostUxIcon('FFXInfo') },
    submenuIconProps: { iconName: useFFXHostUxIcon(localeDirection === 'rtl' ? 'FFXChevronLeft' : 'FFXChevronRight') },
    subMenuProps: {
      items: [
        {
          onRender: () => {
            return <ActionMenuPreviewButton {...previewProps} />;
          }
        }
      ]
    }
  };
  return previewItemProps;
};

// cspell:ignore submenu theming
